import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Logo } from "../components/marketing/MarketingHeader";
import SEO from "../components/SEO";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const COUNTRIES = ["France", "Espagne", "Portugal", "Italie", "Allemagne", "Royaume-Uni", "Autre"];
const TYPES = ["Professionnel", "Particulier"];

const NotEligiblePage = () => {
  const [form, setForm] = useState({ name: "", email: "", type: "", country: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.type || !form.country) {
      setError("Veuillez remplir tous les champs.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError("Une erreur est survenue. Veuillez réessayer.");
      }
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Manrope'] flex flex-col">
      <SEO page="notEligible" />
      {/* Header */}
      <header className="backdrop-blur-xl bg-white/90 border-b border-neutral-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="flex justify-between items-center h-16">
            <Logo />
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-6 md:px-12 py-16 md:py-24">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-[#19294C] transition-colors mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Link>

          {submitted ? (
            <div className="text-center py-12" data-testid="lead-success">
              <div className="w-16 h-16 bg-[#00B8E4]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#00B8E4]" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#19294C] font-['Outfit']">
                Merci !
              </h1>
              <p className="mt-4 text-neutral-600 leading-relaxed">
                Nous vous contacterons dès que HostProtect sera disponible pour vous.
              </p>
              <Link to="/" className="inline-block mt-8">
                <Button
                  variant="outline"
                  className="rounded-full px-6 border-[#19294C] text-[#19294C]"
                  data-testid="back-home-btn"
                >
                  Retour à l'accueil
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h1
                className="text-3xl md:text-4xl font-bold tracking-tight text-[#19294C] font-['Outfit'] leading-tight"
                data-testid="not-eligible-title"
              >
                Nous n'y sommes pas encore...
              </h1>
              <p className="text-xl text-[#00B8E4] font-['Outfit'] font-medium mt-1">
                mais presque.
              </p>
              <p className="mt-4 text-neutral-600 leading-relaxed">
                HostProtect n'est pas encore disponible dans votre région ou
                pour votre profil. Inscrivez-vous pour être informé dès que
                nous serons prêts.
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="lead-form">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-[#19294C]">
                    Nom complet
                  </Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Jean Dupont"
                    className="mt-1.5 rounded-xl border-neutral-200 focus:border-[#00B8E4] focus:ring-[#00B8E4]"
                    data-testid="lead-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-[#19294C]">
                    Adresse email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="jean@exemple.com"
                    className="mt-1.5 rounded-xl border-neutral-200 focus:border-[#00B8E4] focus:ring-[#00B8E4]"
                    data-testid="lead-email-input"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-[#19294C]">
                    Vous êtes
                  </Label>
                  <div className="mt-1.5 flex gap-3">
                    {TYPES.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setForm({ ...form, type: t })}
                        className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                          form.type === t
                            ? "border-[#00B8E4] bg-[#00B8E4] text-white"
                            : "border-neutral-200 text-neutral-600 hover:border-[#00B8E4]"
                        }`}
                        data-testid={`lead-type-${t.toLowerCase()}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="country" className="text-sm font-medium text-[#19294C]">
                    Pays
                  </Label>
                  <select
                    id="country"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    className="mt-1.5 w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-700 focus:outline-none focus:ring-2 focus:ring-[#00B8E4] focus:border-transparent"
                    data-testid="lead-country-select"
                  >
                    <option value="">Sélectionnez votre pays</option>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                {error && (
                  <p className="text-sm text-red-500" data-testid="lead-error">
                    {error}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#00B8E4] text-white rounded-full py-3 hover:bg-[#00a5ce] font-semibold"
                  data-testid="lead-submit-btn"
                >
                  {loading ? "Envoi..." : "Rejoindre la liste d'attente"}
                </Button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default NotEligiblePage;
